import React, { FC, ReactNode, useEffect, useState } from 'react';
import { AddUniversal, TrashCanOutlined } from '@duxton-web/icons';
import {
  Button,
  ConfigProvider,
  Flex,
  Space,
  TimePicker,
  Tooltip,
} from '@duxton-web/ui';
import dayjs from 'dayjs';

import InputControlProps from '../InputControlProps';
import {
  convertIntervalsToString,
  Interval,
  parseIntervals,
  SelectTimeIntervalState,
  SINGAPORE_OFFSET_HOURS,
} from './timeIntervalUtils';

import './SelectTimeInterval.css';

import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const DATE_FORMAT: string = 'HH:mm';

export const getLocalTime = (HHmm) =>
  dayjs(HHmm, DATE_FORMAT).format(DATE_FORMAT);

export const parseCustomOffsetTime = (HHmm, offset) =>
  dayjs(HHmm, DATE_FORMAT)
    .add(-dayjs().utcOffset(), 'minutes')
    .add(offset, 'minutes')
    .format(DATE_FORMAT);

const SelectTimeInterval: FC<InputControlProps> = ({
  value,
  disabled,
  onChange,
}) => {
  const [intervals, setIntervals] = useState<Interval[]>([]);
  const [offsetHours, setOffsetHours] = useState<number>(
    SINGAPORE_OFFSET_HOURS,
  );

  useEffect(() => {
    const { intervals, offsetHours }: SelectTimeIntervalState =
      parseIntervals(value);

    if (!intervals.length) {
      intervals.push({ start: '', end: '' });
    }

    setIntervals(intervals);
    setOffsetHours(offsetHours);
  }, []);

  function onUpdate(inputIntervals: Interval[]): void {
    onChange(convertIntervalsToString(inputIntervals, offsetHours));
  }

  function onAdd(): void {
    const intervalsCopy = JSON.parse(JSON.stringify(intervals));

    intervalsCopy.push({ start: '', end: '' });

    setIntervals(intervalsCopy);
    onUpdate(intervalsCopy);
  }

  function onTimePickerChanged(
    index: number,
    key: string,
    interval: string,
  ): void {
    const intervalsCopy = JSON.parse(JSON.stringify(intervals));

    intervalsCopy[index][key] = interval;

    setIntervals(intervalsCopy);

    onUpdate(intervalsCopy);
  }

  function onRemove(idx: number): void {
    const intervalsCopy = JSON.parse(JSON.stringify(intervals));

    intervalsCopy.splice(idx, 1);

    setIntervals(intervalsCopy);

    onUpdate(intervalsCopy);
  }

  function renderDeleteButton(
    start: string,
    end: string,
    index: number,
  ): ReactNode {
    return (
      <Tooltip
        placement="rightBottom"
        title={`Remove interval ${start || 'N/A'}-${end || 'N/A'}`}
      >
        <ConfigProvider
          theme={{
            token: {
              fontWeightStrong: 400,
            },
            components: {
              Button: {
                onlyIconSize: 14,
                defaultBorderColor: '#bfbfbf',
                defaultBg: '#FFF',
              },
            },
          }}
        >
          <Button
            shape="default"
            onClick={() => onRemove(index)}
            icon={<TrashCanOutlined />}
            disabled={disabled || index === 0}
            style={{
              backgroundColor: '#fff',
            }}
          />
        </ConfigProvider>
      </Tooltip>
    );
  }

  return (
    <Flex
      vertical
      gap={8}
      style={{ width: '100%', overflow: 'auto', marginRight: '4px' }}
    >
      {intervals.map(({ start, end }: Interval, index: number) => (
        <Space.Compact key={index}>
          {/*  Every interval consists of start and end time 
                    kindly let me know if the two layer mapping seems too hard to read */}
          {['start', 'end'].map((intervalFieldKey: string) => {
            const item = { start, end }[intervalFieldKey];

            return (
              <TimePicker
                placeholder={DATE_FORMAT}
                key={intervalFieldKey}
                format={DATE_FORMAT}
                disabled={disabled}
                onChange={(_, value: string) =>
                  onTimePickerChanged(index, intervalFieldKey, value)
                }
                value={item ? dayjs(getLocalTime(item), DATE_FORMAT) : null}
              />
            );
          })}
          {renderDeleteButton(start, end, index)}
        </Space.Compact>
      ))}

      <ConfigProvider
        theme={{
          token: {
            fontWeightStrong: 400,
          },
          components: {
            Button: {
              contentFontSize: 14,
              defaultBorderColor: '#bfbfbf',
            },
          },
        }}
      >
        <Button
          shape="default"
          onClick={onAdd}
          icon={<AddUniversal />}
          disabled={disabled}
          style={{
            width: 'fit-content',
            backgroundColor: '#fff',
          }}
        >
          Add new interval
        </Button>
      </ConfigProvider>
    </Flex>
  );
};

export default SelectTimeInterval;
